import React from 'react';
import Main from 'components/page/Main';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import NavigationUtils from 'utils/NavigationUtils';

import { createStore, applyMiddleware, compose } from 'redux';
import thunkMiddleware from 'redux-thunk';
import createSagaMiddleware from 'redux-saga';
import { routerMiddleware, connectRouter, ConnectedRouter } from 'connected-react-router';
import { combineReducers } from 'redux';

import TabsService from 'services/TabsService';
import ProcessService from 'services/ProcessService';
import DodService from 'services/DodService';
import SecurityService from 'services/SecurityService';
import ModalsService from 'services/ModalsService';
import SiteFacilitiesService from 'services/SiteFacilitiesService';
import RecommendedDataService from 'services/RecommendedDataService';
import DataExplorerService from 'services/DataExplorerService';
import rootSaga from 'services/sagas/RootSaga';
import { injectGlobal } from 'emotion';
import { colorIconBlue } from 'styles/base';
import 'styles/main.less';

// Adding Promise polyfill
require('es6-promise/auto');

const baseUrl = process.env.REACT_APP_BASE_URL;
const windowName = process.env.REACT_APP_WINDOW_URL;

// Define global styles (we need to do this to override some ant design styles)
injectGlobal`
  body {
    font-family: "Muli", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 13px;
    color: #444;
    h1, h2, h3, h4, h5, h6 {
        color: #0D1B2A;
    }
    .ant-message {
        z-index: 5000;
    }
    a, a:hover, a:active {
        color: ${colorIconBlue};
    }
  }
`;

class Application {

    start() {
        console.log(`Starting PCM Process using base URL: ${baseUrl}`);

        let dependencyView = false;
        const segs = window.location.pathname.split('/');
        if (segs[1] === 'dependencies') {
            dependencyView = true;
        }

        const history = NavigationUtils.getHistory();
        const store = this.getStore({}, history);

        window.PCM = {
            appContainer: document.getElementById('root'),
            store
        };

        //for prod build c3js library fails to load the d3 library via "require(d3)" for some reason, but if we
        //load it here first it works.  Webpack should be able to do this for us but config I tried didn't work
        //on first attempt and since we're under deadline and this works, leaving as is for now.
        window.d3 = require('d3');

        if (localStorage.getItem('sessionId') && !dependencyView) {
            SecurityService.loginSSO();
        }

        const pollingInterval = 10 * 6000; // 60 seconds in ms
        SecurityService.dispatchPollRequest(pollingInterval); //
        DataExplorerService.startPolling(); // start polling for pending data explorer requests

        ReactDOM.render(
            <Provider store={store}>
                <ConnectedRouter history={history}>
                    <Main/>
                </ConnectedRouter>
            </Provider>,
            window.PCM.appContainer
        );

        window.addEventListener('beforeunload', this.onUnload);

        // Change the name of our window so that it's easy to toggle back and forth between DOD & Process
        // pages as if they were integrated application
        if (dependencyView) {
            window.name = `${windowName}/dependencies`;
        } else {
            window.name = windowName;
        }
    }

    onUnload = (event) => {
        const storeState = window.PCM.store.getState();
        const myState = TabsService.getMyState(storeState);
        const processState = ProcessService.getMyState(storeState);

        const dirtyTabs = [];
        myState.openTabs.forEach(tabId => {
            const processUIState = processState.processUIStateById[tabId];
            if (processUIState && processUIState.dirty && processUIState.dirty === true) {
                dirtyTabs.push(tabId);
            }
        });

        if (dirtyTabs.length > 0) {
            event.returnValue = 'One or more open processes have unsaved changes.';
        }

    };

    getRootReducer(history) {
        return combineReducers({
            router: connectRouter(history),
            [TabsService.getStoreKey()]: TabsService.getReducer(),
            [ProcessService.getStoreKey()]: ProcessService.getReducer(),
            [DodService.getStoreKey()]: DodService.getReducer(),
            [SecurityService.getStoreKey()]: SecurityService.getReducer(),
            [ModalsService.getStoreKey()]: ModalsService.getReducer(),
            [SiteFacilitiesService.getStoreKey()]: SiteFacilitiesService.getReducer(),
            [DataExplorerService.getStoreKey()]: DataExplorerService.getReducer(),
            [RecommendedDataService.getStoreKey()]: RecommendedDataService.getReducer()
        });
    }

    getStore({ middleware = [] } = {}, history) {
        const sagaMiddleware = createSagaMiddleware();
        const storeEnhancers = [];

        if (process.env.NODE_ENV !== 'production') {
            // middleware.push(require('redux-logger')());
            storeEnhancers.push(
                typeof window === 'object' &&
                typeof window.devToolsExtension !== 'undefined' ? window.devToolsExtension() : f => f
            );
        }

        const store = createStore(
            this.getRootReducer(history),
            compose(
                applyMiddleware(thunkMiddleware, sagaMiddleware, routerMiddleware(history), ...middleware),
                ...storeEnhancers
            )
        );
        sagaMiddleware.run(rootSaga);
        return store;
    }
}

new Application().start();



// WEBPACK FOOTER //
// ./src/index.js